import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../features/auth/services/authService';
import {
  beginTotemSessionReconnect,
  notifyTotemSessionExpired,
  TOTEM_SESSION_EXPIRED_EVENT,
  totemNeedsReconnect,
} from '../services/totemSessionRecovery';
import { totemModeService } from '../services/totemModeService';

/**
 * Só em modo totem: se a sessão de TV expirar de verdade (ou falhar renovação),
 * inicia reconexão rápida via QR em /televisao.
 */
export function useTotemSessionExpiryRecovery(enabled: boolean): void {
  const navigate = useNavigate();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !totemModeService.isTotemMode()) {
      return;
    }

    const startReconnect = () => {
      if (startedRef.current || totemNeedsReconnect()) {
        return;
      }
      startedRef.current = true;
      beginTotemSessionReconnect(navigate);
    };

    const onExpired = () => startReconnect();
    window.addEventListener(TOTEM_SESSION_EXPIRED_EVENT, onExpired);

    const checkSession = async () => {
      if (!totemModeService.isTotemMode() || startedRef.current || totemNeedsReconnect()) {
        return;
      }

      const token = authService.getToken();
      if (!token) {
        notifyTotemSessionExpired('sem-token');
        return;
      }

      // Só token de TV (QR). Login de gestor no display não dispara este fluxo.
      if (!authService.isTelevisaoToken(token)) {
        return;
      }

      // Ainda válido → ok
      if (!authService.isTokenExpiringOrExpired(token, 0)) {
        return;
      }

      try {
        await authService.refreshToken(token);
      } catch {
        notifyTotemSessionExpired('refresh-falhou');
      }
    };

    void checkSession();
    const intervalId = window.setInterval(() => {
      void checkSession();
    }, 60_000);

    return () => {
      window.removeEventListener(TOTEM_SESSION_EXPIRED_EVENT, onExpired);
      window.clearInterval(intervalId);
    };
  }, [enabled, navigate]);
}
